import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

import { Box, Flex, Icon, ICON_LIST, Text } from 'Components/Base';
import Tooltip from 'Components/Tooltip';
import { BarHoverTooltip, useBarHoverTooltip } from 'Components/BarHoverTooltip';

import AxisTick from '../AxisTick';

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <Box
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #C4DEEE',
        padding: '8px 10px',
        fontSize: '12px',
        fontWeight: 600,
      }}
    >
      {payload[0].value.toFixed(2)}%
    </Box>
  );
};

export const MasteryBarChart = ({ activeBar, setActiveBar, mastery }) => {
  const { t } = useTranslation('learningResult');
  const theme = useContext(ThemeContext);
  const { hoveredBar, barHoverHandlers } = useBarHoverTooltip();
  const { LV1, LV2, LV3, LV4 } = mastery;

  const [width, setWidth] = useState(window.innerWidth);

  const data = [
    { name: t('MASTERY_LV1'), value: LV1 },
    { name: t('MASTERY_LV2'), value: LV2 },
    { name: t('MASTERY_LV3'), value: LV3 },
    { name: t('MASTERY_LV4'), value: LV4 },
  ];

  useEffect(() => {
    let timeout;

    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setWidth(window.innerWidth);
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Box
      style={{ width: '100%', borderRadius: '10px', border: `1px solid ${theme.colors.divider}` }}
    >
      <Flex
        style={{
          backgroundColor: theme.colors.primary,
          color: 'white',
          padding: '8px',
          justifyContent: 'center',
          alignItems: 'center',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
        }}
      >
        <Text style={{ fontSize: '18px', fontWeight: 600 }}>{t('MASTERY')} (%)</Text>
        <Tooltip
          arrow
          placement="right-start"
          title={
            <>
              <Text
                as="span"
                style={{
                  fontFamily: 'Noto Sans Thai',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {t('MASTERY_CHART_TOOLTIP')}
              </Text>
            </>
          }
        >
          <Box>
            <Icon
              name={ICON_LIST.INFO_CIRCLE}
              size={20}
              color="white"
              style={{ marginLeft: '4px', cursor: 'pointer', marginTop: '8px' }}
            />
          </Box>
        </Tooltip>
      </Flex>
      <Box style={{ padding: '8px' }}>
        <Box style={{ height: '271px', position: 'relative' }}>
          <ResponsiveContainer key={width}>
            <BarChart
              data={data}
              margin={{ top: 0, right: 0, bottom: 0, left: -35 }}
              barCategoryGap="26.5%"
            >
              <CartesianGrid stroke={theme.colors.chartGrid} />
              <XAxis
                dataKey="name"
                tickMargin={8}
                tickLine={false}
                axisLine={false}
                tick={<AxisTick fill="black" fontSize={12} fontWeight={600} />}
              />
              <YAxis
                tickLine={false}
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                axisLine={false}
                tick={<AxisTick fill="black" fontSize={10} />}
              />
              <Bar dataKey="value" isAnimationActive {...barHoverHandlers}>
                {data.map(entry => {
                  const isActive = entry.name === activeBar?.label;

                  return (
                    <Cell
                      key={entry.name}
                      cursor="pointer"
                      radius={[2, 2, 2, 2]}
                      fill={
                        !activeBar
                          ? theme.colors.progressBarPassBg
                          : isActive
                          ? theme.colors.progressBarPassBg
                          : `${theme.colors.progressBarPassBg}4D`
                      }
                      onClick={() => {
                        setActiveBar(prev =>
                          prev?.label === entry.name ? null : { label: entry.name, type: 'MASTERY' }
                        );
                      }}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <BarHoverTooltip hoveredBar={hoveredBar}>
            <CustomTooltip active payload={[{ value: hoveredBar?.value }]} />
          </BarHoverTooltip>
        </Box>
        <Flex style={{ justifyContent: 'center', marginTop: '12px' }}>
          <Flex
            style={{
              padding: '8px 16px',
              columnGap: '4px',
              alignItems: 'center',
              borderRadius: '9px',
              backgroundColor: theme.colors.neutralGrey,
            }}
          >
            <Box
              style={{
                backgroundColor: theme.colors.progressBarPassBg,
                width: '18px',
                height: '18px',
                flexShrink: 0,
                borderRadius: '4px',
              }}
            />
            <Text style={{ fontSize: '14px' }}>{t('MASTERY_LEGEND')}</Text>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default MasteryBarChart;
